import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { DatePicker } from 'antd';
import 'moment/locale/pt-br';
import locale from 'antd/lib/date-picker/locale/pt_BR';

import { HiBadgeCheck } from 'react-icons/hi';

import caution_icon from '../../assets/general/caution_icon.svg';
import success_icon from '../../assets/general/success_icon.svg';

import left_arrow from '../../assets/general/left_arrow.svg';

import {
  AddButton,
  ActionButton,
  Close,
  Container,
  Goback,
  Incidents,
  AddIncident,
  OrderInfo,
  SeeMoreButton,
  Table,
  Label,
  SaveButton,
  OrderOperators,
} from './_orderDetails';

import { Invoice } from '../../Interfaces/RouteScripitingInterface';
import { BoxSelect } from '../BoxSelect';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { dateMask } from '../../utils/Masks';

interface Props {
  invoice: Invoice | undefined;
  setShowOrderDetails: Function;
}

export interface Occurrence {
  id: string;
  type: string;
  invoice_id: string;
  created_at: string;
  updated_at: string;
  description: string;
  image_url?: string;
}

interface IOperator {
  step: string;
  user: {
    individual_person: {
      name: string;
    };
  };
  updated_at: string;
}

export const OrderDetails: React.FC<Props> = ({
  invoice,
  setShowOrderDetails,
}) => {
  const dateFormat = 'DD/MM/YYYY';

  const [infoSelected, setInfoSelected] = useState<string>('order-info');
  const [loading, setLoading] = useState<boolean>(false);

  const [operators, setOperators] = useState([] as IOperator[]);

  const [occurrences, setOccurrences] = useState<Occurrence[]>();
  const [occurrenceData, setOccurrenceData] = useState({
    type: '',
    description: '',
    image: undefined as File | undefined,
    invoice_id: invoice?.id,
  });

  const [freezeInvoice, setFreezeInvoice] = useState({
    delivery_type: 'no-action',
    appointment_date: '',
    note: '',
    reason: '',
  });

  const [activeImage, setActiveImage] = useState('');

  const receiptRef = useRef<HTMLInputElement>(null);

  const [showBar, setShowBar] = useState(false);
  const [barCode, setBarCode] = useState('Leia o código de barras...');

  const [showNote, setShowNote] = useState(false);
  const [receipt, setReceipt] = useState<File | undefined>();

  const onBarChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    setBarCode(value);
  };

  const onBarSubmit = async () => {
    setLoading(true);
    try {
      await api.post(`/invoices/${invoice?.id}/confirm-invoice-access-key`, {
        collection_information_id:
          invoice?.collection_orders?.[0]?.collection_information?.id,
        access_key: barCode,
      });

      toast.success('Nota adicionada com sucesso!');

      window.location.reload();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Opps algo deu errado!');
    } finally {
      setLoading(false);
    }
  };

  const onBarSelect = () => {
    setShowNote(false);
    setShowBar((prevState) => !prevState);
  };

  const onReceiptChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;

    if (files && files?.length > 0) {
      setReceipt(files[0]);

      return;
    }

    setReceipt(undefined);
  };

  const handleReceipt = async () => {
    // setLoading(true);

    if (receipt && invoice && invoice.id) {
      // const formData = new FormData();
      // formData.append('image', receipt);
      // formData.append('invoice_id', invoice.id);
      // try {
      //   await api.post(`/invoice-receipts`, formData);
      //   toast.success('Nota fiscal adicionada ao pedido com sucesso!')
      // } catch (error: any) {
      //   toast.error(error?.response?.data?.message || 'Opps ocorreu um erro, tente novamente mais tarde!')
      // }
    }

    // setLoading(false);
  };

  const getOperators = async () => {
    try {
      const response = await api.get(`/invoices/${invoice?.id}/operators`);

      const operators = response.data || [];

      setOperators(operators);
    } catch (error: any) {
      handleError(error.response?.data?.message || error.toString());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    infoSelected === 'incidents-list' && getOccurrences();
  }, [infoSelected]);

  useEffect(() => {
    getOperators();
    setOccurrences(undefined);
  }, []);

  const getOccurrences = async () => {
    setLoading(true);
    try {
      const response = await api.get(`occurrences`, {
        params: { invoice_id: invoice?.id },
      });

      if (response.data.length) {
        setOccurrences(response.data);
      }
    } catch (error: any) {
      handleError(error.response?.data?.message || error.toString());
    } finally {
      setLoading(false);
    }
  };

  const handleDate = (value: any) => {
    if (value) {
      const isoDate = new Date(value._d).toISOString();

      setFreezeInvoice({ ...freezeInvoice, appointment_date: isoDate });
    } else {
      setFreezeInvoice({ ...freezeInvoice, appointment_date: '' });
    }
  };

  const handleError = (error: string) => {
    toast.error(error);
  };

  const saveOccurrence = async () => {
    setLoading(true);

    const formData = new FormData();

    formData.append('type', occurrenceData.type);

    if (occurrenceData.invoice_id) {
      formData.append('invoice_id', occurrenceData.invoice_id);
    }

    if (occurrenceData.description) {
      formData.append('description', occurrenceData.description);
    }

    if (occurrenceData.image) {
      formData.append('image', occurrenceData.image);
    }

    try {
      const response = await api.post(`occurrences`, formData);

      if (response) {
        toast.success('Ocorrência adicionada');
        setInfoSelected('incidents-list');
      }
    } catch (error: any) {
      handleError(error.response?.data?.message || error.toString());
    } finally {
      setLoading(false);
    }
  };

  const saveFreezeInvoice = async () => {
    setLoading(true);
    try {
      const response = await api.patch(
        `invoices/${invoice?.id}/delivery-type`,
        freezeInvoice,
      );

      if (response) {
        toast.success('Nota congelada com sucesso!');
      }
    } catch (error: any) {
      handleError(error.response?.data?.message || error.toString());
    } finally {
      setLoading(false);
    }
  };

  const handleFreeStorage = async () => {
    const truckId =
      invoice?.collection_orders?.[0]?.collection_information?.truck_id || '';

    try {
      await api.post('/invoices/start-discharge', {
        truck_id: truckId,
      });

      toast.info('Pronto para armazenagem!');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Opps algo deu errado!');
    }
  };

  const handleFreeLoading = async () => {
    const truckRouteId = invoice?.route_item?.truck_route?.id || '';

    try {
      await api.put(`/truck-routes/${truckRouteId}/ready`);

      toast.info('Pronto para carregamento!');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Opps algo deu errado!');
    }
  };

  const handleFreeLocal = async () => {
    const id = invoice?.id || '';

    try {
      await api.post('local-deliveries', {
        invoice_ids: [id],
      });

      toast.info('Liberado para entrega local!');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Opps algo deu errado!');
    }
  };

  const setLocalDelivery = async () => {
    setLoading(true);

    try {
      const response = await api.patch(
        `invoices/${invoice?.id}/delivery-type`,
        { delivery_type: 'local' },
      );

      if (response) {
        toast.success('Alteração salva com sucesso!');
      }
    } catch (error: any) {
      handleError(error.response?.data?.message || error.toString());
    } finally {
      setLoading(false);
    }
  };

  const renderOrderInformations = () => {
    return (
      <>
        <div className="order-info__table-wrapper">
          <Table className="table">
            <thead>
              <tr>
                <th>N° do Pedido</th>

                <th>Situação</th>

                <th>Cliente</th>

                <th>Endereço da entrega</th>

                <th>N° da nota</th>

                <th>Último operador</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td data-label="N° do Pedido">{invoice?.order_number}</td>

                <td data-label="Situação">{invoice?.status}</td>

                <td data-label="Cliente">{invoice?.client_name}</td>

                <td data-label="Endereço de entrega">{invoice?.address}</td>

                <td data-label="N° da nota">{invoice?.invoice_number}</td>

                <td data-label="Nome do operador">
                  <span className="table__information-span">
                    {invoice?.invoice_operators &&
                      invoice?.invoice_operators[
                        invoice.invoice_operators.length - 1
                      ].user?.individual_person?.name}
                  </span>
                </td>
              </tr>
            </tbody>
          </Table>

          <div className="order-info__table-wrapper__buttons-wrapper">
            <AddButton onClick={() => setInfoSelected('add-incident')}>
              Adicionar ocorrências
            </AddButton>
            <SeeMoreButton onClick={() => setInfoSelected('incidents-list')}>
              Ver ocorrências
            </SeeMoreButton>
          </div>
        </div>

        <div className="order-info__titled-table-wrapper">
          <span className="order-info__table-title">Liberação de Volumes</span>

          <div className="order-info__table-wrapper">
            <Table className="table">
              <thead>
                <tr>
                  <th>Inconsistência</th>

                  {/* <th>Tipo de caixa</th> */}

                  <th>Quantidade de etiquetas lidas</th>

                  {/* <th>Quantidade liberada</th> */}

                  <th>Operações</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td data-label="Inconsistência">
                    {invoice?.amount_boxes !== invoice?.amount_read_labels ? (
                      <img
                        className="table__status-icon"
                        src={caution_icon}
                        alt="inconsitência"
                      />
                    ) : (
                      <img
                        className="table__status-icon"
                        src={success_icon}
                        alt="sucesso"
                      />
                    )}
                  </td>

                  {/* <td data-label="Tipo de caixa">XXXXXX</td> */}

                  <td data-label="Qtde de etiquedas lidas">
                    {invoice?.amount_read_labels || '00'}/
                    {invoice?.amount_boxes}
                  </td>

                  {/* <td data-label="Qtde liberada">02</td> */}

                  <td data-label="Operações">
                    <span className="table__actions-wrapper">
                      <span
                        className="table__actions-wrapper__action free-item"
                        onClick={() => handleSketch(invoice && invoice.id)}
                      >
                        Liberar coleta
                      </span>

                      <span
                        className="table__actions-wrapper__action free-item"
                        onClick={() => handleFreeStorage()}
                      >
                        Liberar armazenagem
                      </span>

                      {invoice?.route_item?.truck_route?.id && (
                        <span
                          className="table__actions-wrapper__action free-item"
                          onClick={() => handleFreeLoading()}
                        >
                          Liberar carregamento
                        </span>
                      )}

                      <span
                        className="table__actions-wrapper__action free-item"
                        onClick={() => handleFreeLocal()}
                      >
                        Liberar entrega local
                      </span>

                      {/* <span className="table__actions-wrapper__action delete">
                        <img src={fail_icon} alt="deletar" />
                        Excluir
                      </span> */}
                    </span>
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>

          {showNote && (
            <div className="order-info__buttons">
              <ActionButton
                className="action-button--local-delivery"
                disabled={loading}
                onClick={() => receiptRef?.current?.click()}
              >
                {receipt ? receipt.name : 'Selecione o arquivo'}
              </ActionButton>
              <input
                type="file"
                ref={receiptRef}
                style={{ display: 'none' }}
                onChange={onReceiptChange}
              />

              <ActionButton
                className="action-button--withheld-invoice"
                disabled={loading}
                onClick={handleReceipt}
              >
                {loading ? 'Editando...' : 'Salvar'}
              </ActionButton>
            </div>
          )}

          {showBar && (
            <div className="order-info__buttons">
              {/* <h2 className="order-info__bar-code">
              {barCode}
            </h2> */}

              <input
                type="text"
                onChange={onBarChange}
                className="action-input__barcode"
                placeholder="Digite o código de barras"
              />

              {/* <Scanner onDetected={onBarChange} /> */}

              <ActionButton
                className="action-button--withheld-invoice"
                disabled={loading}
                onClick={onBarSubmit}
              >
                {loading ? 'Editando...' : 'Salvar'}
              </ActionButton>
            </div>
          )}

          <div className="order-info__buttons">
            {!invoice?.invoice_number && (
              <ActionButton
                className="action-button--local-delivery"
                disabled={loading}
                onClick={() => onBarSelect()}
              >
                Ler código de barras
              </ActionButton>
            )}

            {/* {!invoice?.invoice_number && (
            <ActionButton
              className="action-button--local-delivery"
              disabled={loading}
              onClick={() => {
                setShowBar(false)
                setShowNote(prevState => (!prevState))
              }}
            >
              Incluir nota
            </ActionButton>
            )} */}

            <ActionButton
              className="action-button--local-delivery"
              disabled={loading}
              onClick={() => setLocalDelivery()}
            >
              {loading ? 'Editando...' : 'Entrega Local'}
            </ActionButton>

            <ActionButton
              className="action-button--withheld-invoice"
              onClick={() => setInfoSelected('freeze-invoice')}
            >
              Reter Nota
            </ActionButton>

            {/* <ActionButton onClick={() => setInfoSelected('add-boxes')}>
              Incluir volume
            </ActionButton> */}
          </div>
        </div>
      </>
    );
  };

  const renderIncidents = () => {
    return (
      <Incidents>
        <Goback
          src={left_arrow}
          alt="voltar"
          onClick={() => setInfoSelected('order-info')}
        />

        <div className="incidents__title">Ocorrências</div>

        <Table className="table">
          <thead>
            <tr>
              <th>Data</th>

              {/* <th>Operador</th> */}

              <th>Tipo de ocorrencia</th>

              <th>Observação</th>

              <th></th>
            </tr>
          </thead>
          <tbody>
            {occurrences && occurrences?.length > 0 ? (
              occurrences.map(
                ({ updated_at, type, description, image_url }) => {
                  return (
                    <tr>
                      <td data-label="Data">
                        {new Date(updated_at).toLocaleString()}
                      </td>

                      {/* <td data-label="Operador">Marcelo</td> */}

                      {/* <td data-label="Cliente">Rua do Louvre, 555</td> */}

                      <td data-label="Tipo de ocorrência">{type}</td>

                      <td data-label="Observação">{description}</td>

                      {image_url && (
                        <td data-label="Image">
                          <button
                            type="button"
                            className="occurrence-image__button"
                            onClick={() => {
                              if (image_url) {
                                setActiveImage(image_url);

                                setInfoSelected('incident-image');
                              }
                            }}
                          >
                            Ver imagem
                          </button>
                        </td>
                      )}
                      {/*
                <td>
                  <SeeImage>
                    <img src={img_icon} alt="foto" />
                    Imagem
                  </SeeImage>
                </td> */}
                    </tr>
                  );
                },
              )
            ) : (
              <tr>Nenhuma ocorrência encontada</tr>
            )}
          </tbody>
        </Table>
      </Incidents>
    );
  };

  const renderIncidentImage = () => {
    return (
      <Incidents>
        <Goback
          src={left_arrow}
          alt="voltar"
          onClick={() => setInfoSelected('incidents-list')}
        />
        <img src={activeImage} alt="Occurrence" className="incident__image" />
      </Incidents>
    );
  };

  const renderAddIncident = () => {
    return (
      <AddIncident className="new-incident">
        <Goback
          src={left_arrow}
          alt="voltar"
          onClick={() => setInfoSelected('order-info')}
        />

        <div className="add-incident__title">Nova ocorrencia de pedido</div>

        <div className="add-incident__field-group">
          <Label className="field-label">
            Tipo de ocorrência
            <input
              type="text"
              className="field-label__input"
              onChange={(e) =>
                setOccurrenceData({ ...occurrenceData, type: e.target.value })
              }
            />
          </Label>
        </div>

        <div className="add-incident__field-group">
          <Label className="field-label extended">
            Descrição
            <input
              type="text"
              className="field-label__input"
              onChange={(e) =>
                setOccurrenceData({
                  ...occurrenceData,
                  description: e.target.value,
                })
              }
            />
          </Label>
        </div>

        <div className="add-incident__field-group">
          <Label className="field-label extended">
            Imagem
            <p className="field-label-value">
              {occurrenceData?.image
                ? occurrenceData?.image?.name
                : 'Selecione a imagem'}
            </p>
            <input
              type="file"
              style={{ display: 'none' }}
              className="field-label__input"
              onChange={(e) => {
                const { files } = e.target;

                if (files && files.length > 0) {
                  setOccurrenceData((prevState) => ({
                    ...prevState,
                    image: files[0],
                  }));

                  return;
                }

                setOccurrenceData((prevState) => ({
                  ...prevState,
                  image: undefined,
                }));
              }}
            />
          </Label>
        </div>

        <SaveButton disabled={loading} onClick={() => saveOccurrence()}>
          <HiBadgeCheck size={24} />
          {loading ? 'Salvando...' : 'Salvar'}
        </SaveButton>
      </AddIncident>
    );
  };

  const renderOrderOperatorsList = () => {
    return (
      <OrderOperators className="order-operators ">
        <div className="order-operators__title">
          Usuários operadores de pedido
        </div>

        <Table className="table">
          <thead>
            <tr>
              <th>Data</th>

              <th>Operador</th>

              {/* <th>Perfil</th> */}

              <th>Tipo de operação</th>

              <th></th>
            </tr>
          </thead>
          <tbody>
            {operators?.length > 0 ? (
              operators?.map((item) => (
                <tr>
                  <td data-label="Data">{dateMask(item?.updated_at)}</td>

                  <td data-label="Operador">
                    {item?.user?.individual_person?.name}
                  </td>

                  {/* <td data-label="Perfil">{item?.step}</td> */}

                  <td data-label="Tipo de operação">{item?.step}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td data-label="Data">Nenhuma operação encontrada!</td>
              </tr>
            )}
          </tbody>
        </Table>
      </OrderOperators>
    );
  };

  const renderFreezeInvoice = () => {
    return (
      <AddIncident className="new-incident">
        <Goback
          src={left_arrow}
          alt="voltar"
          onClick={() => setInfoSelected('order-info')}
        />

        <div className="add-incident__title">Reter nota</div>

        <div className="add-incident__field-group">
          <Label className="field-label">
            Motivo
            <input
              type="text"
              className="field-label__input"
              onChange={(e) =>
                setFreezeInvoice({
                  ...freezeInvoice,
                  note: e.target.value,
                })
              }
            />
          </Label>

          <Label className="field-label">
            Data do agendamento
            <DatePicker
              onChange={handleDate}
              placeholder="dd/mm/aaa"
              format={dateFormat}
              showToday={false}
              locale={locale}
              showTime={{ format: 'HH:mm' }}
            />
          </Label>
        </div>

        <div className="add-incident__field-group">
          <Label className="field-label extended">
            Observação
            <input
              type="text"
              className="field-label__input"
              onChange={(e) =>
                setFreezeInvoice({
                  ...freezeInvoice,
                  reason: e.target.value,
                })
              }
            />
          </Label>
        </div>

        <SaveButton onClick={() => saveFreezeInvoice()}>
          <HiBadgeCheck size={24} />
          Reter nota
        </SaveButton>
      </AddIncident>
    );
  };

  const boxSelection = () => {
    return (
      <>
        <Goback
          src={left_arrow}
          alt="voltar"
          onClick={() => setInfoSelected('order-info')}
        />
        <BoxSelect disableSelect={false} edit={false} invoiceID={invoice?.id} />
      </>
    );
  };

  const handleSelectedInfo = (infoSelected: string) => {
    const panel: { [key: string]: any } = {
      'order-info': renderOrderInformations(),
      'incidents-list': renderIncidents(),
      'incident-image': renderIncidentImage(),
      'add-incident': renderAddIncident(),
      'order-operators-list': renderOrderOperatorsList(),
      'add-boxes': boxSelection(),
      'freeze-invoice': renderFreezeInvoice(),
    };

    return panel[infoSelected];
  };

  const handleSketch = async (id: string | undefined) => {
    if (!id) return;

    setLoading(true);
    try {
      const response = await api.put(`invoices/${id}/collect`);

      if (response) {
        toast.success('Nota liberada com sucesso');
      }
    } catch (error: any) {
      handleError(error.response?.data?.message || error.toString());
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="order-details">
      <Close className="close" onClick={() => setShowOrderDetails(false)}>
        {'\u00D7'}
      </Close>
      <OrderInfo className="order-info">
        <div className="order-info__buttons-wrapper">
          <div
            className={`order-info__tab-button ${
              infoSelected !== 'order-operators-list' && 'active'
            }`}
            onClick={() => setInfoSelected('order-info')}
          >
            Prontuário do Pedido
          </div>
          <div
            className={`order-info__tab-button ${
              infoSelected === 'order-operators-list' && 'active'
            }
            )}`}
            onClick={() => setInfoSelected('order-operators-list')}
          >
            Usuários Operadores
          </div>
        </div>

        {handleSelectedInfo(infoSelected)}
      </OrderInfo>
    </Container>
  );
};
